import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useSafeMutation } from "~/common/useSafeMutation"
import { useHomePath } from "~/common/useHomePath"
import { LoadingIndicatorCentered } from "~/components/LoadingIndicator"
import { Button } from "~/shadcn/ui/button"
import Text, { H1 } from "~/ui/typography"
import { ROSTER_PLAN_ACTIVATE } from "../api"

/**
 * Talent agencies are on the free Roster plan and never choose a plan
 * (ZAN-893). Onboarding provisions it inline but swallows failures, so a talent
 * agency that reaches this screen has no plan yet: activate it here, and on
 * failure show an error with a retry rather than falling back to a picker.
 */
export const ActivateRosterPlan = () => {
  const navigate = useNavigate()
  const homePath = useHomePath()
  const [runActivate] = useSafeMutation(ROSTER_PLAN_ACTIVATE)
  const startedRef = useRef(false)
  const [activated, setActivated] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const activate = useCallback(async () => {
    setError(null)
    const { data, errors } = await runActivate()
    if (errors || !data) {
      console.error("Errors activating Roster plan:", errors)
      setError(errors?.[0]?.message ?? "Something went wrong.")
      return
    }
    setActivated(true)
  }, [runActivate])

  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true
    activate()
  }, [activate])

  // Navigate once the mutation's user has landed in the cache, so homePath
  // reflects the new plan's permissions.
  useEffect(() => {
    if (activated) navigate(homePath)
  }, [activated, homePath, navigate])

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <H1 className="mb-2">We couldn't set up your account</H1>
        <Text variant="header3" className="mb-8">
          {error}
        </Text>
        <Button type="button" onClick={activate}>
          Try again
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <H1 className="mb-2">Setting up your account…</H1>
      <Text variant="header3" className="mb-8">
        Hang tight while we activate your free Roster plan.
      </Text>
      <LoadingIndicatorCentered />
    </div>
  )
}
